export interface CountryItem {
  EN: string;
  IT: string;
  SELECT: string;
  DE?: string;
  ES?: string;
  FR?: string;
}

// Languages that can carry their own country name; "it"/"en" are the original two.
const COUNTRY_LANGUAGES = ["IT", "EN", "DE", "ES", "FR"] as const;

// country list has no column for (or an empty one) falls back to SELECT, which
// is what every language other than it/en used before.
export const getCountryLabel = (
  item: CountryItem,
  language: string
): string => {
  const key = language.split("-")[0].toUpperCase();
  if ((COUNTRY_LANGUAGES as readonly string[]).includes(key)) {
    const label = item[key as keyof CountryItem];
    if (label) return label;
  }
  return item.SELECT;
};

// Italy as it can appear in the country dropdown: Italian/Spanish, English,
// German and French. The check is case-insensitive.
const ITALY_NAMES = ["ITALIA", "ITALY", "ITALIEN", "ITALIE"];

export const isItalyCountry = (name?: string | null): boolean =>
  typeof name === "string" && ITALY_NAMES.includes(name.toUpperCase());

export const toCountryName = (
  items: CountryItem[] | undefined,
  name: string
): string => {
  if (!name || !items?.length) return name;
  const item = items.find((i) => matchesCountryName(i, name, true));
  if (!item) return name;
  const key = name.toLowerCase();
  if (item.IT?.toLowerCase() === key || item.EN?.toLowerCase() === key) {
    return name;
  }
  return item.SELECT || name;
};

// Maps the named country fields of a payload through toCountryName; every other
// key is left as it is, and a field that is empty or absent is not added.
export const mapCountryFields = <T extends Record<string, any>>(
  payload: T,
  items: CountryItem[] | undefined,
  keys: string[]
): T => {
  const mapped: Record<string, any> = { ...payload };
  keys.forEach((key) => {
    if (typeof mapped[key] === "string" && mapped[key]) {
      mapped[key] = toCountryName(items, mapped[key]);
    }
  });
  return mapped as T;
};

// True when `name` is this country's name in any supported language — used to
// map the label chosen in the form back to the SELECT value the backend expects.
export const matchesCountryName = (
  item: CountryItem | undefined,
  name: string | undefined,
  caseInsensitive = false
): boolean => {
  if (!item) return false;
  return COUNTRY_LANGUAGES.some((lang) => {
    const label = item[lang];
    // A language the list has no column for must never match an empty name.
    if (!label) return false;
    if (caseInsensitive) {
      return label.toLowerCase() === name?.toLowerCase();
    }
    return label === name;
  });
};

// Prefers an exact SELECT match before falling back to matching any
// language column, so the canonical value never loses to a translated one.
export const findCountryItem = (
  items: CountryItem[],
  name: string,
  caseInsensitive = false
): CountryItem | undefined =>
  items.find((item) => item.SELECT === name) ??
  items.find((item) => matchesCountryName(item, name, caseInsensitive));
